// IndexedDB keystore for the NC1 identity — the browser twin of Windows'
// DPAPI keystore. Holds the UNWRAPPED identity secrets once the user has
// unlocked (at signup, via passkey PRF, device-transfer, or recovery code),
// so steady-state page loads decrypt without prompting.
//
// Same at-rest posture as Virgil e3kit's IndexedDB private-key store today.
// Note Account.vue's clearAllIndexedDB() (account deletion/reset) wipes this
// too — by design; the identity re-enters via passkey/transfer/recovery.

import localforage from 'localforage'

const store = localforage.createInstance({
  name: 'nc1-keystore',
})

const identityKey = (accountId) => `identity.${accountId}`

/**
 * secrets = { id_enc_pk, id_enc_sk, id_sign_pk, id_sign_sk, identity_version }
 * (the exact snake_case shape the §7.1 wraps protect).
 */
export async function saveIdentity(accountId, { secrets, altAccountId, email }) {
  await store.setItem(identityKey(accountId), {
    secrets,
    altAccountId,
    email,
    savedAt: Date.now(),
  })
}

export async function loadIdentity(accountId) {
  return await store.getItem(identityKey(accountId))
}

export async function clearIdentity(accountId) {
  await store.removeItem(identityKey(accountId))
}

// ---- local passkey marker ---------------------------------------------------
// WebAuthn has no silent "is one of these credentials usable here?" query, so
// at registration time we remember which credential THIS browser minted. Lets
// the UI say "a passkey protects this device ✓" instead of offering to mint a
// duplicate. Best-effort: cleared storage just means the UI offers Add again.

const passkeyKey = (accountId) => `passkey.${accountId}`

export async function recordLocalPasskey(accountId, credentialIdB64) {
  await store.setItem(passkeyKey(accountId), {
    credentialIdB64,
    registeredAt: Date.now(),
  })
}

export async function getLocalPasskey(accountId) {
  return await store.getItem(passkeyKey(accountId))
}

// ---- pending "Link a device" request ----------------------------------------
// The requester's one-time transfer key pair used to live in page memory only,
// so a reload, a closed tab or a phone freezing the tab killed the request
// while the approver still saw it, and approving it went nowhere
// (user-reported 2026-09-29). It now survives here, in the same store that
// holds the unwrapped identity once the link succeeds, until the grant
// arrives, the request is cancelled or rejected, or it ages past the server's
// 24-hour life. Shape: see DeviceLink.serializeLink.

const pendingLinkKey = (accountId) => `link.${String(accountId).toLowerCase()}`

export async function savePendingLink(accountId, link) {
  await store.setItem(pendingLinkKey(accountId), { ...link, savedAt: Date.now() })
}

export async function loadPendingLink(accountId) {
  return await store.getItem(pendingLinkKey(accountId))
}

export async function clearPendingLink(accountId) {
  await store.removeItem(pendingLinkKey(accountId))
}

// ---- invite links the OWNER created (INVITE-LINK-PASSKEY-FIRST.md §1.5) ----
// The link's fragment code exists nowhere else, so "Copy link again" on the
// recipients page needs this browser to have kept it. No new exposure: this
// store already holds the owner's identity, which opens everything the link
// can. Dropped when the invite is claimed, revoked or expired.

const inviteLinkKey = (ownerEmail, recipientEmail) =>
  `invite.${String(ownerEmail).toLowerCase()}.${String(recipientEmail).toLowerCase()}`

export async function saveInviteLink(ownerEmail, recipientEmail, link) {
  await store.setItem(inviteLinkKey(ownerEmail, recipientEmail), { ...link, savedAt: Date.now() })
}

export async function loadInviteLink(ownerEmail, recipientEmail) {
  return await store.getItem(inviteLinkKey(ownerEmail, recipientEmail))
}

export async function clearInviteLink(ownerEmail, recipientEmail) {
  await store.removeItem(inviteLinkKey(ownerEmail, recipientEmail))
}
